import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';

const NOTAS = [
  { valor: 1, rotulo: 'Muito ruim' },
  { valor: 2, rotulo: 'Ruim' },
  { valor: 3, rotulo: 'Regular' },
  { valor: 4, rotulo: 'Bom' },
  { valor: 5, rotulo: 'Excelente' },
];

const MAX_TENTATIVAS = 20;
const INTERVALO_MS = 3000;

const PerguntaNota = ({ numero, texto, valor, onChange, extra }) => (
  <fieldset className="space-y-3">
    <legend className="text-base font-semibold text-slate-900">
      {numero}. {texto}
    </legend>
    <div className="grid grid-cols-1 gap-2">
      {NOTAS.map((nota) => {
        const ativo = valor === nota.valor;
        return (
          <button
            key={nota.valor}
            type="button"
            onClick={() => onChange(nota.valor)}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${
              ativo
                ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                : 'border-slate-200 bg-white text-slate-700'
            }`}
          >
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
              ativo ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'
            }`}>
              {nota.valor}
            </span>
            {nota.rotulo}
          </button>
        );
      })}
      {extra}
    </div>
  </fieldset>
);

const PesquisaPublica = () => {
  const { token } = useParams();
  const [estado, setEstado] = useState('carregando');
  const [recarga, setRecarga] = useState(0);
  const [notaServico, setNotaServico] = useState(null);
  const [notaTecnico, setNotaTecnico] = useState(null);
  const [notaEquipamento, setNotaEquipamento] = useState(null);
  const [naoAvaliaEquipamento, setNaoAvaliaEquipamento] = useState(false);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState('');

  useEffect(() => {
    let cancelado = false;
    let timer;
    let tentativa = 0;

    const carregar = async () => {
      try {
        const { data } = await api.get(`/public/pesquisa/${encodeURIComponent(token)}`);
        if (cancelado) return;
        setEstado(data?.ja_respondida ? 'obrigado' : 'formulario');
      } catch (erro) {
        if (cancelado) return;
        const status = erro.response?.status;
        tentativa += 1;
        const aindaPodeEsperar = (status === 404 || !status) && tentativa < MAX_TENTATIVAS;
        if (aindaPodeEsperar) {
          setEstado('aguardando');
          timer = setTimeout(carregar, INTERVALO_MS);
          return;
        }
        setEstado(status === 404 ? 'nao_encontrada' : 'erro');
      }
    };

    setEstado('carregando');
    carregar();
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [token, recarga]);

  const equipamentoRespondido = naoAvaliaEquipamento || notaEquipamento != null;
  const podeEnviar = notaServico != null && notaTecnico != null && equipamentoRespondido && !enviando;

  const enviar = async (event) => {
    event.preventDefault();
    if (!podeEnviar) return;
    setEnviando(true);
    setErroEnvio('');
    try {
      await api.post(`/public/pesquisa/${encodeURIComponent(token)}`, {
        nota_servico: notaServico,
        nota_tecnico: notaTecnico,
        nota_equipamento: naoAvaliaEquipamento ? null : notaEquipamento,
        equipamento_nao_avaliado: naoAvaliaEquipamento,
        comentario: comentario.trim(),
      });
      setEstado('obrigado');
    } catch (erro) {
      if (erro.response?.status === 409) {
        setEstado('obrigado');
        return;
      }
      setErroEnvio(erro.response?.data?.detail || 'Não foi possível enviar. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  };

  const escolherEquipamento = (valor) => {
    setNaoAvaliaEquipamento(false);
    setNotaEquipamento(valor);
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-6">
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">Puroá</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">Avaliação do atendimento técnico da Puroá</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Sua opinião nos ajuda a acompanhar a qualidade dos nossos serviços e melhorar continuamente. Leva menos de 1 minuto.
          </p>
        </div>

        {(estado === 'carregando' || estado === 'aguardando') && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
            <p className="mt-4 text-sm text-slate-600">
              {estado === 'aguardando'
                ? 'Estamos preparando sua avaliação. Isso leva só um instante.'
                : 'Carregando...'}
            </p>
          </div>
        )}

        {(estado === 'nao_encontrada' || estado === 'erro') && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-slate-700">
              {estado === 'nao_encontrada'
                ? 'Não encontramos esta avaliação. Se o comprovante acabou de ser impresso, aguarde um pouco e tente de novo.'
                : 'Não foi possível abrir a avaliação agora.'}
            </p>
            <button
              type="button"
              onClick={() => setRecarga((n) => n + 1)}
              className="mt-4 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white"
            >
              Tentar de novo
            </button>
          </div>
        )}

        {estado === 'obrigado' && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm">
            <h2 className="text-xl font-bold text-slate-900">Obrigado</h2>
            <p className="mt-2 text-sm text-slate-600">Sua avaliação foi registrada.</p>
          </div>
        )}

        {estado === 'formulario' && (
          <form onSubmit={enviar} className="space-y-6 rounded-2xl bg-white p-5 shadow-sm">
            <PerguntaNota
              numero={1}
              texto="Como você avalia a qualidade do serviço realizado hoje?"
              valor={notaServico}
              onChange={setNotaServico}
            />
            <PerguntaNota
              numero={2}
              texto="Como você avalia o técnico quanto ao conhecimento, cuidado e clareza ao explicar o serviço?"
              valor={notaTecnico}
              onChange={setNotaTecnico}
            />
            <PerguntaNota
              numero={3}
              texto="O equipamento está atendendo às necessidades do laboratório?"
              valor={naoAvaliaEquipamento ? null : notaEquipamento}
              onChange={escolherEquipamento}
              extra={(
                <button
                  type="button"
                  onClick={() => {
                    setNaoAvaliaEquipamento(true);
                    setNotaEquipamento(null);
                  }}
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-medium ${
                    naoAvaliaEquipamento
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                      : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  Não consigo avaliar
                </button>
              )}
            />
            <div>
              <label htmlFor="comentario" className="block text-base font-semibold text-slate-900">
                Comentário (opcional)
              </label>
              <p className="mt-1 text-sm text-slate-500">Quer comentar algo ou explicar alguma nota?</p>
              <textarea
                id="comentario"
                value={comentario}
                maxLength={2000}
                rows={4}
                onChange={(event) => setComentario(event.target.value)}
                className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500"
              />
            </div>
            {erroEnvio && <p className="text-sm text-rose-700">{erroEnvio}</p>}
            <button
              type="submit"
              disabled={!podeEnviar}
              className="w-full rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-40"
            >
              {enviando ? 'Enviando...' : 'Enviar avaliação'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PesquisaPublica;
